import { useEffect, useMemo } from 'react';

import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { DateTime } from 'luxon';
import { useDispatch, useSelector } from 'react-redux';

import { useAppSelector } from 'src/app/store/hooks';
import type { RootState } from 'src/app/store/types';
import { usePaginationState } from 'src/common/DataGrid/usePaginationState';
import {
  useAssignmentsListQuery,
  useVehiclesListQuery,
} from 'src/common/external/bambi-api/bambiApi';

import AssignmentDownloadModal from './AssignmentDownloadModal';
import {
  AssignmentManagementDisplayMode,
  setAssignments,
  setSelectedAssignmentRows,
  setSelectedDay,
} from './assignmentManagement.slice';
import { AssignmentManagementDataGrid } from './AssignmentManagementDisplay/AssignmentManagementDataGrid/AssignmentManagementDataGrid';
import { AssignmentManagementPlanBy } from './AssignmentManagementDisplay/AssignmentManagementPlanBy';
import { AssignmentManagementForm } from './AssignmentManagementForm/AssignmentManagementForm';
import { AssignmentManagementToolbar } from './AssignmentManagementToolbar';
import { MultiAssignmentManagementForm } from './MultiAssignmentManagementForm/MultiAssignmentManagementForm';

export function AssignmentManagementRoute() {
  const dispatch = useDispatch();
  const { selectedDay, assignmentSearchTerm } = useSelector(
    (state: RootState) => state.assignmentManagement
  );

  const selectedDayOrToday = selectedDay || DateTime.local().toISODate();

  const displayMode = useAppSelector(
    (state) => state.assignmentManagement.displayMode
  );

  const filterDrivers = useAppSelector(
    (state) => state.assignmentManagement.filterDrivers
  );

  const filterVehicles = useAppSelector(
    (state) => state.assignmentManagement.filterVehicles
  );

  const dataGridSelectedRange = useAppSelector(
    (state) => state.assignmentManagement.dataGridSelectedRange
  );

  const isPastDate =
    DateTime.now().startOf('day') > DateTime.fromISO(selectedDayOrToday);

  const rangeStart = useMemo(() => {
    if (displayMode === AssignmentManagementDisplayMode.PLAN_BY) {
      return DateTime.fromISO(selectedDayOrToday)
        .startOf('day')
        .toUTC()
        .toISO();
    }

    if (dataGridSelectedRange) {
      return DateTime.fromISO(dataGridSelectedRange.from)
        .startOf('day')
        .toUTC()
        .toISO();
    }

    return undefined;
  }, [displayMode, selectedDayOrToday, dataGridSelectedRange]);

  const rangeEnd = useMemo(() => {
    if (displayMode === AssignmentManagementDisplayMode.PLAN_BY) {
      return DateTime.fromISO(selectedDayOrToday).endOf('day').toUTC().toISO();
    }

    if (dataGridSelectedRange) {
      return DateTime.fromISO(dataGridSelectedRange.to)
        .endOf('day')
        .toUTC()
        .toISO();
    }

    return undefined;
  }, [displayMode, selectedDayOrToday, dataGridSelectedRange]);

  const { pagination, handlePaginationChange, paramsWithPage } =
    usePaginationState({
      drivers:
        displayMode === AssignmentManagementDisplayMode.DATAGRID
          ? filterDrivers.join(',')
          : undefined,
      vehicles:
        displayMode === AssignmentManagementDisplayMode.DATAGRID
          ? filterVehicles.join(',')
          : undefined,
      search: assignmentSearchTerm,
      rangeStart,
      rangeEnd,
    });

  const {
    data: assignments,
    error: assignmentsError,
    isLoading: assignmentsLoading,
    isFetching: assignmentsFetching,
  } = useAssignmentsListQuery(paramsWithPage);

  const {
    data: vehicles,
    error: vehiclesError,
    isLoading: vehiclesLoading,
  } = useVehiclesListQuery({
    canBeScheduled: true,
    deletedObjects: isPastDate ? 'include' : undefined,
  });

  useEffect(() => {
    dispatch(setAssignments(assignments?.results || []));
  }, [dispatch, assignments]);

  // set selected day to today on mount
  // This is set in the initial state in the store,
  // but the org's timezone may not be set yet as Settings.defaultZone
  useEffect(() => {
    if (!selectedDay) {
      dispatch(setSelectedDay(DateTime.local().toISODate()));
    }
  }, [dispatch, selectedDay]);

  useEffect(() => {
    dispatch(setSelectedAssignmentRows({}));
  }, [dispatch, filterDrivers, filterVehicles, dataGridSelectedRange]);

  const assignmentDate = DateTime.fromISO(selectedDayOrToday);

  // only want to display vehicles that are not deleted
  // or have a deleted_at date that is before the selected day
  const displayVehicles =
    (vehicles?.results ?? []).filter((vehicle) => {
      if (!vehicle.deleted_at) return true;

      const deletedAt = DateTime.fromISO(vehicle.deleted_at).startOf('day');
      const selectedDateTime =
        DateTime.fromISO(selectedDayOrToday).startOf('day');

      return deletedAt >= selectedDateTime;
    }) || [];

  if (assignmentsError || vehiclesError) {
    return <div>Something went wrong</div>;
  }

  return (
    <>
      {assignmentsLoading || vehiclesLoading || assignmentsFetching ? (
        <div className="flex h-[70vh] w-[96vw] items-center justify-center">
          <LoadingIndicator />
        </div>
      ) : (
        <>
          {displayMode === AssignmentManagementDisplayMode.PLAN_BY && (
            <>
              <AssignmentManagementToolbar
                assignmentDate={assignmentDate}
                setSelectedDate={(date) => {
                  dispatch(setSelectedDay(date.toISODate()));
                }}
              />

              <div className="mx-[-2rem]">
                <AssignmentManagementPlanBy
                  vehicles={displayVehicles || []}
                  assignments={assignments?.results || []}
                  assignmentDate={assignmentDate}
                />
              </div>
            </>
          )}
          {displayMode === AssignmentManagementDisplayMode.DATAGRID && (
            <div className="mt-4">
              <AssignmentManagementDataGrid
                assignments={assignments?.results || []}
                totalCount={assignments?.count || 0}
                pagination={pagination}
                handlePaginationChange={handlePaginationChange}
              />
            </div>
          )}
        </>
      )}
      <AssignmentManagementForm />
      <MultiAssignmentManagementForm />
      <AssignmentDownloadModal />
    </>
  );
}
